import React, { useEffect, useState } from 'react'
import { useQuery } from '@apollo/client'
import { useHistory } from 'react-router-dom'
import Grid from '@material-ui/core/Grid'
import Box from '@material-ui/core/Box'
import LinearProgress from '@material-ui/core/LinearProgress'
import LocationDropdown from '../../../schedule/SelectLessonType/location-dropdown'
import { get } from 'lodash'
import BookingTypesList from './booking-types-list'
import LibraryBooksOutlinedIcon from 'components/material-symbol/icons/LibraryBooks'
import EmptyBookingTypes from 'components/empty-page-state'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { useTheme } from '@material-ui/core/styles'
import useStyles from './styles'
import InfoAlert from 'components/info-alert'
import Typography from '@material-ui/core/Typography'
import Button from '@material-ui/core/Button'
import { GET_LESSON_TYPES_AND_COACH_FACILITIES } from './queries'
import { useUpgradeFlow } from 'lib/hooks/use-upgrade-flow'
import UpgradeSuccessModal from 'components/upgrade-success-modal'

const BookingTypesViewWrapper = ({
  children,
  loadingSubscription,
  handleUpgradeClick,
  showDisclaimer,
}) => {
  const classes = useStyles()

  return (
    <>
      <Grid item xs={12}>
        {loadingSubscription && <LinearProgress color="primary" />}
        {showDisclaimer ? (
          <InfoAlert
            mb={2}
            variant="promo"
            className={classes.upgradePromoCallout}
            title="Want to Add More Offerings?"
            action={
              <Button
                variant="contained"
                color="primary"
                onClick={handleUpgradeClick}
              >
                Upgrade Now
              </Button>
            }
          >
            <Typography variant="body2">
              Enjoy the benefits of your free account with up to two booking
              types. Upgrade to unlock unlimited booking types.
            </Typography>
          </InfoAlert>
        ) : null}
      </Grid>
      {children}
    </>
  )
}

const BookingTypesView = ({ setShouldShowDisclaimer }) => {
  const classes = useStyles()
  const history = useHistory()
  const isMobile = useMediaQuery(useTheme().breakpoints.only('xs'))
  const [selectedFacility, setSelectedFacility] = useState('all')
  const [coachFacilities, setCoachFacilities] = useState([])
  const [bookingTypes, setBookingTypes] = useState([])
  const [showDisclaimer, setShowDisclaimer] = useState(false)
  const { data, loading, refetch } = useQuery(
    GET_LESSON_TYPES_AND_COACH_FACILITIES,
    { fetchPolicy: 'cache-and-network' },
  )

  const {
    upgradeLoading: loadingSubscription,
    handleUpgradeClick,
    showSuccessModal,
    handleDismiss,
  } = useUpgradeFlow('booking-types')

  useEffect(() => {
    if (loading === false && data) {
      const coachAvailabilities = data.coachAvailabilities || []
      const cf = coachAvailabilities.map(cf => {
        return { id: cf.coachFacility.id, name: cf.coachFacility.name }
      })
      const freeTierLimitsReached = get(
        data,
        'currentUser.attributes.freeTierLimitsReached',
        [],
      )
      const isBookingTypesLimitReached = freeTierLimitsReached.includes(
        'BOOKING_TYPES',
      )
      setBookingTypes(data.lessonTypes)
      setCoachFacilities(cf)
      setShouldShowDisclaimer(isBookingTypesLimitReached)
      setShowDisclaimer(isBookingTypesLimitReached)
    }
  }, [loading, data, setShouldShowDisclaimer])

  if (loading) {
    return (
      <BookingTypesViewWrapper>
        <LinearProgress color="primary" />
      </BookingTypesViewWrapper>
    )
  }

  const isEmpty = bookingTypes.length === 0

  const hasAvailabilityConfigured = data.coachAvailabilities?.some(
    a => a.timeSlots.length > 0,
  )

  if (isEmpty && hasAvailabilityConfigured) {
    return (
      <EmptyBookingTypes
        actionFn={() => {
          history.push('/pga-coach/bookings/booking-types/new')
        }}
        IconComp={() => <LibraryBooksOutlinedIcon fontSize="large" />}
        title="Create a booking type"
        caption="Take the first step to online customer bookings. A booking type represents a coaching program you offer (e.g. private instruction). Get started today!"
        buttonText="Create Booking Type"
        removeButton={isMobile}
      />
    )
  }

  if (isEmpty && !hasAvailabilityConfigured) {
    return (
      <EmptyBookingTypes
        actionFn={() => {
          history.push('/pga-coach/settings/availability')
        }}
        IconComp={() => <LibraryBooksOutlinedIcon fontSize="large" />}
        title="Create a booking type"
        caption="Before creating a booking type, you must set your availability"
        buttonText="Set Availability"
        removeButton={isMobile}
      />
    )
  }

  return (
    <>
      <BookingTypesViewWrapper
        {...{
          loadingSubscription,
          handleUpgradeClick,
          showDisclaimer,
        }}
      >
        <Grid item xs={12}>
          <BookingTypesList
            selectedFacility={selectedFacility}
            bookingTypes={bookingTypes}
            refetch={refetch}
            locationFilter={
              <Box className={classes.locationWrapper}>
                <LocationDropdown
                  selectedFacility={selectedFacility}
                  setSelectedFacility={setSelectedFacility}
                  locations={coachFacilities}
                />
              </Box>
            }
          />
        </Grid>
      </BookingTypesViewWrapper>

      <UpgradeSuccessModal isOpen={showSuccessModal} onClose={handleDismiss} />
    </>
  )
}

export default BookingTypesView
